import { useCallback, useEffect } from "react";
import { useLatest } from "hooks/useLatest";
import { useRequest } from "hooks/useRequest";
import { useResetPaginationOnDepsChange } from "./hooks";
import type {
  UsePaginatedRequestCoreArgs,
  PaginatedResponse,
  GoToPage,
  SetItemsPerPage,
} from "./types";

export const usePaginatedRequestCore = <TItem>({
  page,
  setPage,
  limit,
  setLimit,
  defaultLimit = 20,
  resetDeps,
  service,
}: UsePaginatedRequestCoreArgs<TItem>) => {
  const serviceRef = useLatest(service);

  const {
    data: requestData,
    error: requestError,
    isLoading: requestIsLoading,
    run,
  } = useRequest<PaginatedResponse<TItem>, [{ page: number; limit: number }]>(
    async ({ page, limit }) => serviceRef.current({ page, limit }),
    { manual: true }
  );

  const pagination = requestData?.pagination;
  const pages = pagination?.pages ?? 1;
  const total = pagination?.total ?? 0;

  const canPrev = page > 1;
  const canNext = page < pages;

  const goToPage: GoToPage = useCallback(
    (next) => {
      const safe = Number.isFinite(next) && next > 0 ? next : 1;
      setPage(pages ? Math.min(safe, pages) : safe);
    },
    [pages, setPage]
  );

  const nextPage = useCallback(() => {
    if (!canNext) return;
    setPage(page + 1);
  }, [canNext, page, setPage]);

  const prevPage = useCallback(() => {
    if (!canPrev) return;
    setPage(page - 1);
  }, [canPrev, page, setPage]);

  const setItemsPerPage: SetItemsPerPage = useCallback(
    (nextLimit) => {
      const safe =
        Number.isFinite(nextLimit) && nextLimit > 0 ? nextLimit : defaultLimit;
      setLimit(safe);
      setPage(1);
    },
    [defaultLimit, setLimit, setPage]
  );

  const refresh = useCallback(() => {
    run({ page, limit });
  }, [run, page, limit]);

  useEffect(() => {
    run({ page, limit });
  }, [run, page, limit]);

  useResetPaginationOnDepsChange({
    resetDeps,
    page,
    limit,
    setPage,
    run,
  });

  return {
    // request state
    data: requestData,
    error: requestError,
    isLoading: requestIsLoading,

    // pagination state
    page,
    limit,

    // server meta
    pagination,
    pages,
    total,

    // controls
    goToPage,
    nextPage,
    prevPage,
    canNext,
    canPrev,
    setItemsPerPage,

    // actions
    refresh,
    run,
  };
};
